import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  FileText, Check, Zap, Upload, Download, 
  Shield, Globe, BarChart3, Users, Clock,
  CheckCircle, AlertCircle, Mail, Smartphone,
  ArrowRight, FileCheck, Stamp, RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const InvoicingPage = () => {
  const features = [
    {
      icon: FileText,
      title: 'Emisión Individual y Masiva',
      description: 'Emite facturas, boletas, notas de crédito y débito de forma individual o masiva mediante archivo CSV'
    },
    {
      icon: Stamp,
      title: 'Gestión de Folios CAF',
      description: 'Solicita, descarga y gestiona folios automáticamente sin salir de la plataforma. Conectado al SII'
    },
    {
      icon: Shield,
      title: 'Certificado Digital Centralizado',
      description: 'Centraliza tu certificado digital para operar con normalidad ante el SII desde cualquier dispositivo'
    },
    {
      icon: Download,
      title: 'Recepción de DTEs',
      description: 'Gestiona facturas de proveedores con reglas automáticas de aceptación o rechazo'
    },
    {
      icon: RefreshCw,
      title: 'Cesión de Documentos',
      description: 'Realiza cesiones de facturas y envíalas directamente al SII con seguimiento completo'
    },
    {
      icon: Globe,
      title: 'DTE de Exportación',
      description: 'Emite facturas y notas de crédito de exportación cumpliendo normativa internacional'
    }
  ];

  const dteTypes = [
    'Facturas Electrónicas',
    'Boletas Electrónicas',
    'Boletas de Honorarios',
    'Notas de Crédito',
    'Notas de Débito',
    'Guías de Despacho',
    'Facturas de Compra',
    'Facturas de Exportación',
    'Liquidaciones'
  ];

  const platforms = [
    {
      icon: Globe,
      title: 'Web',
      description: 'Utiliza tu navegador preferido con diseño responsivo para celulares'
    },
    {
      icon: Smartphone,
      title: 'Desktop',
      description: 'Descarga la aplicación a tu computador con módulos adicionales'
    },
    {
      icon: Zap,
      title: 'API REST',
      description: 'Integra tu aplicación como pasarela entre el SII y tus documentos'
    }
  ];

  const benefits = [
    {
      stat: '100%',
      description: 'Integrado con SII Chile'
    },
    {
      stat: '<1 seg',
      description: 'Tiempo de emisión'
    },
    {
      stat: '24/7',
      description: 'Soporte en línea'
    },
    {
      stat: 'Ilimitado',
      description: 'Documentos mensuales'
    }
  ];

  const additionalFeatures = [
    {
      icon: AlertCircle,
      title: 'Sistema de Alertas',
      description: 'Notificaciones por email y en portal de todas las acciones importantes'
    },
    {
      icon: FileCheck,
      title: 'PDF Personalizados',
      description: 'Plantillas ajustables en formatos oficio, carta, 80mm y 58mm'
    },
    {
      icon: Users,
      title: 'Permisos de Usuario',
      description: 'Crea perfiles y controla accesos de tu equipo'
    },
    {
      icon: Upload,
      title: 'Exportación de Datos',
      description: 'Exporta todos tus XML y PDF cuando lo necesites'
    }
  ];

  const integrationFlow = [
    {
      step: '1',
      title: 'Configura',
      description: 'Sube tu certificado digital y solicita folios al SII desde la plataforma'
    },
    {
      step: '2',
      title: 'Emite',
      description: 'Crea documentos individual o masivamente. Automático con tu sistema via API'
    },
    {
      step: '3',
      title: 'Valida',
      description: 'Validación automática con SII y envío al receptor en tiempo real'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black opacity-90"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
              Facturación Electrónica Chile
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
              Facturación DTE 100% Integrada con SII
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
              Emite facturas, boletas y todos los documentos tributarios electrónicos con validación automática del SII. Simple, rápido y totalmente legal.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Probar Gratis 30 Días
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Ver Demostración
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Certificado por SII</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Sin límite de documentos</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>API incluida</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Stats */}
      <section className="py-20 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-gray-900 mb-2">{benefit.stat}</div>
                <div className="text-gray-600">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Cómo Funciona
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Tres pasos simples para empezar a facturar electrónicamente
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12">
              {integrationFlow.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-green-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Características Principales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Todo lo que necesitas para cumplir con el SII y optimizar tu facturación
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-8">
                    <Icon className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* DTE Types */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Todos los Documentos Tributarios
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Emite todos los tipos de DTE autorizados por el SII
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {dteTypes.map((dte, index) => (
                <div key={index} className="bg-white p-6 border border-gray-200 hover:border-green-600 transition-colors duration-300">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-900 font-medium">{dte}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Múltiples Plataformas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Accede desde donde prefieras: web, desktop o integra con API
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {platforms.map((platform, index) => {
              const Icon = platform.icon;
              return (
                <Card key={index} className="border border-gray-200 text-center hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-10">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {platform.title}
                    </h3>
                    <p className="text-gray-600">
                      {platform.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <a 
              href="#" 
              className="text-green-600 hover:text-green-700 font-semibold inline-flex items-center gap-2"
            >
              Ver documentación de API
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
              Funcionalidades Adicionales
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-gray-50 p-12 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Soporte Técnico en Línea
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Creemos que los chatbots generan mala experiencia. Habla directamente con nuestro equipo por WhatsApp o solicita que te llamemos al instante.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                onClick={() => window.location.href = '/demo'}
              >
                <Mail className="w-5 h-5 mr-2" />
                Soporte por Email
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 px-8 py-6"
                onClick={() => window.location.href = '/demo'}
              >
                <Smartphone className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-20 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Certificado SII</h3>
                <p className="text-gray-600 text-sm">Plataforma certificada y homologada por Servicio de Impuestos Internos</p>
              </div>
              <div>
                <Clock className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Disponibilidad 99.9%</h3>
                <p className="text-gray-600 text-sm">Infraestructura robusta con uptime garantizado</p>
              </div>
              <div>
                <BarChart3 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Reportes Integrados</h3>
                <p className="text-gray-600 text-sm">Dashboard con métricas y exportación de datos en tiempo real</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl sm:text-6xl font-bold text-white mb-8">
              Comienza a Facturar Hoy
            </h2>
            <p className="text-xl text-gray-300 mb-12 font-light">
              Prueba gratuita de 30 días. Sin tarjeta de crédito. Configuración en minutos.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
                onClick={() => window.location.href = '/demo'}
              >
                Crear Cuenta Gratis
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Hablar con Ventas
              </Button>
            </div>
            
            <p className="mt-8 text-gray-400 text-sm">
              Soporte en español • Integración con tu sistema • Migración gratuita
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InvoicingPage;
